import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { isUser, isCompany, isAdmin } from '../../features/auth/authSlice';
import api from '../../utils/axiosConfig';
import { showToast } from '../../features/ui/uiSlice';
import { formatLogoUrl } from '../../utils/imageUtils';
import {
  BriefcaseIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  BellIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface DashboardStats {
  unreadNotifications: number;
  walletBalance: number;
  totalEarnings: number;
  verifiedEarnings: number;
  pendingEarnings: number;
  taskApplications: number;
  approvedApplications: number;
  invitations: number;
  availableForWithdrawal: number;
  totalWithdrawn: number;
  totalPendingWithdrawals: number;
  totalApprovedWithdrawals: number;
}

interface RecentNotification {
  _id: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  createdAt: string;
}

interface TaskApplication {
  _id: string;
  task: {
    _id: string;
    title: string;
    company: {
      companyName: string;
      logo?: string;
    };
  };
  status: string;
  appliedAt: string;
  assignedDesignation?: string;
}

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);


  const [stats, setStats] = useState<DashboardStats>({
    unreadNotifications: 0,
    walletBalance: 0,
    totalEarnings: 0,
    verifiedEarnings: 0,
    pendingEarnings: 0,
    taskApplications: 0,
    approvedApplications: 0,
    invitations: 0,
    availableForWithdrawal: 0,
    totalWithdrawn: 0,
    totalPendingWithdrawals: 0,
    totalApprovedWithdrawals: 0,
  });
  const [recentNotifications, setRecentNotifications] = useState<RecentNotification[]>([]);
  const [recentApplications, setRecentApplications] = useState<TaskApplication[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch multiple data sources in parallel
      const [
        notificationsResponse,
        unreadCountResponse,
        walletResponse,
        applicationsResponse,
        invitationsResponse
      ] = await Promise.all([
        api.get('/notifications?limit=5'),
        api.get('/notifications/unread-count'),
        api.get('/wallets').catch(() => ({ data: { success: false } })), // Handle 404 gracefully
        api.get('/task-applications/my-applications?limit=5').catch(() => ({ data: { success: false } })), // Handle 404 gracefully
        api.get('/task-applications/my-applications?status=invited').catch(() => ({ data: { success: false } })) // Handle 404 gracefully
      ]);

      // Set recent notifications
      if (notificationsResponse.data.success) {
        setRecentNotifications(notificationsResponse.data.data.notifications || []);
      }

      // Set unread notifications count
      if (unreadCountResponse.data.success) {
        setStats(prev => ({ ...prev, unreadNotifications: unreadCountResponse.data.data.count || 0 }));
      }

      // Set wallet data
      if (walletResponse.data.success) {
        const wallet = walletResponse.data.data;
        setStats(prev => ({
          ...prev,
          walletBalance: wallet?.currentBalance || 0,
          totalEarnings: wallet?.totalEarnings || 0,
          verifiedEarnings: wallet?.verifiedBalance || 0,
          pendingEarnings: wallet?.pendingBalance || 0,
          availableForWithdrawal: wallet?.availableForWithdrawal || 0,
          totalWithdrawn: wallet?.totalWithdrawn || 0,
          totalPendingWithdrawals: wallet?.totalPendingWithdrawals || 0,
          totalApprovedWithdrawals: wallet?.totalApprovedWithdrawals || 0
        }));
      }

      // Set task applications data
      if (applicationsResponse.data.success) {
        const applications = applicationsResponse.data.data.applications || [];
        setRecentApplications(applications);
        setStats(prev => ({
          ...prev,
          taskApplications: applications.length,
          approvedApplications: applications.filter((app: TaskApplication) => app.status === 'approved').length
        }));
      }

      // Set invitations data
      if (invitationsResponse.data.success) {
        const invitations = invitationsResponse.data.data.applications || [];
        setStats(prev => ({
          ...prev,
          invitations: invitations.length
        }));
      }

    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      dispatch(showToast({
        message: 'Failed to load dashboard data',
        type: 'error'
      }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (isUser(user)) return `${user.firstName} ${user.lastName}`;
    if (isCompany(user)) return user.companyName;
    if (isAdmin(user)) return `${user.firstName} ${user.lastName}`;
    return 'User';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'invited':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      case 'applied':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'withdrawn':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'job-invite':
        return <BriefcaseIcon className="h-5 w-5 text-blue-500" />;
      case 'job-application':
        return <UserGroupIcon className="h-5 w-5 text-green-500" />;
      case 'task-assigned':
        return <ClipboardDocumentListIcon className="h-5 w-5 text-purple-500" />;
      case 'payment-approved':
        return <CreditCardIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-8"></div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className=" mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Welcome back, {getUserDisplayName()}!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Here's what's happening with your account today.
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-6 mb-8">
          {/* Total Earnings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-green-500">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Earnings
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  ₹{(stats.totalEarnings || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  All time earnings
                </p>
              </div>
            </div>
          </div>

          {/* Verified Earnings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-blue-500">
                <CheckCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Verified Earnings
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  ₹{(stats.verifiedEarnings || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Available for withdrawal
                </p>
              </div>
            </div>
          </div>

          {/* Wallet Balance */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-purple-500">
                <CreditCardIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Wallet Balance
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  ₹{(stats.walletBalance || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Current balance
                </p>
              </div>
            </div>
          </div>

          {/* Pending Earnings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-yellow-500">
                <ClockIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Pending Earnings
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  ₹{(stats.pendingEarnings || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Awaiting verification
                </p>
              </div>
            </div>
          </div>

          {/* Invitations */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-indigo-500">
                <BellIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Invitations
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {stats.invitations || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Pending invitations
                </p>
              </div>
            </div>
          </div>

          {/* Available for Withdrawal */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-green-500">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Available for Withdrawal
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  ₹{(stats.availableForWithdrawal || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Ready to withdraw
                </p>
              </div>
            </div>
          </div>

          {/* Total Withdrawn */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-blue-500">
                <CheckCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Withdrawn
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  ₹{(stats.totalWithdrawn || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Successfully withdrawn
                </p>
              </div>
            </div>
          </div>

          {/* Pending Withdrawals */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-orange-500">
                <ClockIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Pending Withdrawals
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  ₹{(stats.totalPendingWithdrawals || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Awaiting processing
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* My Applications */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  My Applications
                </h2>
                <a href="/dashboard/applications" className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                  View all
                </a>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentApplications.length > 0 ? (
                  recentApplications.filter(application => application.task).map((application) => (
                    <div key={application._id} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <div className="flex-shrink-0">
                        {application.task?.company?.logo ? (
                          <img
                            src={formatLogoUrl(application.task.company.logo)}
                            alt={application.task.company?.companyName || 'Company'}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                            <span className="text-white text-xs font-medium">
                              {application.task?.company?.companyName?.charAt(0) || 'C'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {application.task?.title || 'Task Deleted'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {application.task?.company?.companyName || 'Unknown Company'}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Applied: {formatDate(application.appliedAt)}
                          {application.assignedDesignation && ` • ${application.assignedDesignation.replace('_', ' ')}`}
                        </p>
                      </div>
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                        {application.status}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CheckCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No applications yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Notifications */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Recent Notifications
                </h2>
                <a href="/dashboard/notifications" className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                  View all
                </a>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentNotifications.length > 0 ? (
                  recentNotifications.map((notification) => (
                    <div key={notification._id} className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${!notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}>
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${!notification.isRead ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'
                          }`}>
                          {notification.title}
                        </p>
                        <p className={`text-sm truncate ${!notification.isRead ? 'text-blue-700 dark:text-blue-200' : 'text-gray-500 dark:text-gray-400'
                          }`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {formatDate(notification.createdAt)}
                        </p>
                      </div>
                      {!notification.isRead && (
                        <div className="flex-shrink-0">
                          <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <BellIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No notifications yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Company-specific content for companies */}
        {user && isCompany(user) && (
          <div className="mt-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Company Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <BuildingOfficeIcon className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    0
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Total Employees
                  </p>
                </div>
                <div className="text-center">
                  <ClipboardDocumentListIcon className="h-12 w-12 text-green-600 dark:text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    0
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Tasks Posted
                  </p>
                </div>
                <div className="text-center">
                  <CreditCardIcon className="h-12 w-12 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    ₹0
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Total Budget
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;